// AI stack model catalog — derived from ai-router (apps/ai-router/src/tiers.ts)
// and LiteLLM /v1/models. Recommended 4-tier consolidation per architecture audit.

export interface AITier {
  level: number;
  name: string;
  costCeiling: string;
  models: string[];
  intent: string;
  color: string;
  recommended: boolean;
}

// CURRENT (deployed) — 7 tiers with significant overlap.
export const CURRENT_TIERS: AITier[] = [
  { level: 0, name: "local",       costCeiling: "$0",          models: ["llama3.1:70b", "deepseek-coder-v2:16b", "qwen2.5-coder:14b"], intent: "Drafts, privacy, batch", color: "#10b981", recommended: true },
  { level: 1, name: "free-cloud",  costCeiling: "$0",          models: ["meta-llama/llama-3.1-8b-instruct:free"], intent: "Prototyping (rate-limited)", color: "#22d3ee", recommended: false },
  { level: 2, name: "micro",       costCeiling: "$1 / 1M",     models: ["gpt-4o-mini", "gemini-flash-1.5", "llama-3.3-70b (Groq)"], intent: "High-volume, extraction", color: "#60a5fa", recommended: true },
  { level: 3, name: "standard",    costCeiling: "$15 / 1M",    models: ["claude-sonnet-4-6", "gpt-4o", "gemini-pro-1.5"], intent: "Most professional work — DEFAULT", color: "#a78bfa", recommended: true },
  { level: 4, name: "premium",     costCeiling: "$60 / 1M",    models: ["claude-opus-4-7", "o3-mini", "gemini-2.5-pro"], intent: "High-stakes reasoning", color: "#e879f9", recommended: false },
  { level: 5, name: "frontier",    costCeiling: "unlimited",   models: ["o3", "claude-opus-4-7", "gemini-2.5-pro"], intent: "Hardest problems (overlaps premium)", color: "#fb7185", recommended: false },
  { level: 6, name: "specialist",  costCeiling: "unlimited",   models: ["deepseek-coder-v2", "pixtral-large", "gpt-4o (vision)"], intent: "Task-specific override", color: "#f59e0b", recommended: false },
];

// RECOMMENDED — 4 tiers, no overlap, clear roles.
export const RECOMMENDED_TIERS: AITier[] = [
  { level: 0, name: "local",     costCeiling: "$0",       models: ["llama3.2", "deepseek-coder-v2:16b", "llava:13b"], intent: "Drafts, private, dev", color: "#10b981", recommended: true },
  { level: 1, name: "cheap",     costCeiling: "$1 / 1M",  models: ["gpt-4o-mini", "gemini-flash-2.0", "llama-3.3-70b (Groq)", "deepseek-chat"], intent: "90% of traffic", color: "#60a5fa", recommended: true },
  { level: 2, name: "standard",  costCeiling: "$15 / 1M", models: ["claude-sonnet-4-6", "gpt-4o", "gemini-pro-1.5"], intent: "Hard tasks", color: "#a78bfa", recommended: true },
  { level: 3, name: "frontier",  costCeiling: "$75 / 1M", models: ["claude-opus-4-7", "o3", "gemini-2.5-pro"], intent: "Rare critical", color: "#fb7185", recommended: true },
];

// Specialist routes (pulled out of being a tier; now a flag).
export const SPECIALISTS = [
  { id: "coder",     models: ["qwen2.5-coder:14b", "codestral:22b", "deepseek-coder-v2", "claude-sonnet-4-6"], capability: "Strong code synthesis" },
  { id: "vision",    models: ["gpt-4o", "claude-sonnet-4-6", "gemini-pro-1.5"], capability: "Image + multimodal understanding" },
  { id: "long-doc",  models: ["claude-opus-4-7", "gemini-pro-1.5"], capability: "Large context windows" },
  { id: "embedding", models: ["text-embedding-3-small", "text-embedding-3-large", "nomic-embed-text"], capability: "Vector embeddings" },
  { id: "reasoning", models: ["o3", "deepseek-r1", "claude-opus-4-7"], capability: "Multi-step planning, math" },
];

export const AI_ROUTER_AGENTS = {
  total: 43,
  categories: { generalist: 6, coding: 10, writing: 4, analysis: 6, ops: 9, creative: 2, orchestrator: 1, domain: 5 },
};

export const LITELLM_FACTS = {
  url: "https://litellm.orangegrass-ad6d20d5.eastus.azurecontainerapps.io",
  registeredModels: 99,
  directKeyModels: 7,
  openRouterRoutedModels: 92,
  ftModel: "ft:gpt-4o-mini-2024-07-18:will-bracken-organization:wb-infra-v2:DbcHIaoL",
};
